import { flatMapDeep, last, map } from 'lodash-es';
import {
  Content,
  CurrentFolderHierarchy,
  FolderHierarchy,
  ROOT_FOLDER_ID,
} from '../components/services/UploadManagementServiceTypes';
import { useMemo } from 'react';

interface FolderHierarchyExtended extends FolderHierarchy {
  parent?: FolderHierarchyExtended;
}

const getFlatFolderHierarchyExtended = (hierarchy: CurrentFolderHierarchy) => {
  if (!hierarchy) {
    return null;
  }
  const getSubHierarchies = (
    h: FolderHierarchy,
    parent?: FolderHierarchyExtended,
  ): FolderHierarchyExtended[] =>
    flatMapDeep(h.subHierarchy, (subHierarchy) => {
      const sub: FolderHierarchyExtended = {
        ...subHierarchy,
        parent: parent ? parent : hierarchy,
      };
      return [sub, getSubHierarchies(subHierarchy, sub)];
    });
  return [hierarchy].concat(getSubHierarchies(hierarchy));
};

const getParents = ({ parent }: FolderHierarchyExtended): FolderHierarchyExtended[] =>
  parent ? [...getParents(parent), parent] : [];

type Path = Array<{ guid: string; name?: string }>;

const getPathToParentContent = ({
  content,
  hierarchy,
}: {
  content: Content;
  hierarchy: FolderHierarchyExtended[] | null;
}): Path => {
  if (!hierarchy) {
    return [];
  }

  const currentParentFolder = hierarchy.find((h) =>
    content.parentGuid ? h.guid === content.parentGuid : h.guid === ROOT_FOLDER_ID,
  );
  if (!currentParentFolder) {
    return [];
  }

  const otherParentsPath = map(getParents(currentParentFolder), ({ guid, name }) => ({
    guid,
    name,
  }));

  return otherParentsPath.concat({
    name: currentParentFolder.name,
    guid: currentParentFolder.guid,
  });
};

export type BreadcrumbItemData = { name?: string; guid: string };
export type BreadcrumbData = BreadcrumbItemData[];

export const usePathFromHierarchy = ({
  folderHierarchy,
  content,
}: {
  folderHierarchy?: CurrentFolderHierarchy;
  content: Content;
}) => {
  const flatFolderHierarchyExtended = useMemo(
    () => getFlatFolderHierarchyExtended(folderHierarchy),
    [folderHierarchy],
  );
  const parentBreadcrumb: BreadcrumbData = useMemo(
    () => getPathToParentContent({ content, hierarchy: flatFolderHierarchyExtended }),
    [content, flatFolderHierarchyExtended],
  );
  const breadcrumb: BreadcrumbData = useMemo(() => {
    if (last(parentBreadcrumb)?.guid === content.guid) {
      return parentBreadcrumb;
    }
    return parentBreadcrumb.concat({ guid: content.guid, name: content.name });
  }, [parentBreadcrumb, content]);
  const pathToParentContent = useMemo(
    () => parentBreadcrumb.map((x) => x.guid),
    [parentBreadcrumb],
  );
  const pathToContent = useMemo(() => breadcrumb.map((x) => x.guid), [breadcrumb]);

  return { pathToParentContent, pathToContent, parentBreadcrumb, breadcrumb };
};
